import { Link } from "react-router-dom";
import { Mic, GraduationCap, Users, BrainCircuit, BookText, TrendingUp, Globe, Shield, ArrowRight } from "lucide-react";

const formats = [
  { icon: Mic, title: "Ponencias para eventos", desc: "Charlas de 30 a 60 minutos para eventos empresariales, tecnológicos o sectoriales. Contenido con tesis propia, estructura clara y ideas que los asistentes se llevan." },
  { icon: GraduationCap, title: "Clases magistrales", desc: "Para escuelas de negocio y universidades. Sesiones de 90 a 120 minutos combinando marco teórico y casos reales de construcción empresarial." },
  { icon: Users, title: "Workshops cerrados", desc: "Sesiones de trabajo con equipos directivos u offsites. Más pequeños, más intensos, más caros y más cualificados. Para empresas que quieren pensar en serio." },
  { icon: BrainCircuit, title: "Sesiones estratégicas privadas", desc: "Conversaciones de alto valor con CEOs, fundadores o consejos asesores. Reflexión sobre modelo, posicionamiento, narrativa y decisiones críticas." },
  { icon: BookText, title: "Mesas redondas y paneles", desc: "Participación como ponente o moderador en debates sobre IA, empresa, empleo, estrategia y geopolítica empresarial." },
  { icon: Globe, title: "Entrevistas y medios", desc: "Disponible para entrevistas en prensa económica y tecnológica, podcasts y programas sobre empresa, tecnología e innovación." },
];

const topics = [
  {
    title: "IA aplicada a dirección y operaciones",
    desc: "Cómo integrar IA con sentido, sin sustituir criterio ni deteriorar la experiencia del cliente. Productividad real vs. espectáculo tecnológico.",
  },
  {
    title: "Liderazgo y empresa en entornos de complejidad",
    desc: "Qué exige dirigir cuando el contexto cambia más rápido que las estructuras organizativas. Decisión, velocidad y claridad de tesis.",
  },
  {
    title: "El futuro del trabajo",
    desc: "Automatización, valor humano y rediseño del rol del directivo. Cómo separar criterio de rutina y construir equipos que amplíen la IA en lugar de ser reemplazados por ella.",
  },
  {
    title: "Human-first como ventaja competitiva",
    desc: "Por qué la confianza, el servicio y la responsabilidad pueden ser mejores activos que el puro canal. Estrategia, no romanticismo.",
  },
  {
    title: "Fundraising y narrativa para inversores",
    desc: "Cómo contar una compañía para atraer capital sin caer en fórmulas vacías. Encajar en la tesis del inversor sin perder la identidad.",
  },
  {
    title: "Storytelling corporativo",
    desc: "Cómo convertir una empresa en un relato con identidad y utilidad. El discurso del CEO como pieza estratégica.",
  },
  {
    title: "Geopolítica empresarial",
    desc: "Cómo los cambios regulatorios, tecnológicos y de poder afectan a las compañías. Energía, telecomunicaciones, soberanía digital y dependencia tecnológica.",
  },
];

const audience = [
  "Comités de dirección",
  "Founders y co-founders",
  "Scaleups en crecimiento",
  "Corporates en transformación",
  "Universidades y escuelas de negocio",
  "Asociaciones empresariales",
  "Eventos sectoriales",
  "Offsites y sesiones privadas",
];

export default function Speaking() {
  return (
    <div className="pt-16 md:pt-20">
      {/* Header */}
      <section data-testid="speaking-header" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-6">Ponencias y colaboraciones</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal leading-[1.1] max-w-3xl mb-8 tracking-tight animate-fade-slide-up">
            Charlas, sesiones estratégicas y colaboraciones para equipos directivos, escuelas de negocio y organizaciones que quieran pensar mejor.
          </h1>
          <p className="text-muted-foreground max-w-xl leading-relaxed">
            No charlas motivacionales. No recetas universales. Ideas con tesis propia,
            experiencia operativa real y un marco de análisis que los participantes pueden
            aplicar al día siguiente.
          </p>
        </div>
      </section>

      {/* Formats */}
      <section data-testid="speaking-formats" className="py-24 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-10">Formatos</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {formats.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} data-testid={`format-${f.title}`} className="bg-background p-8 border border-border hover:border-primary/30 transition-colors">
                  <Icon size={20} className="text-primary mb-5" />
                  <p className="font-serif text-lg mb-3">{f.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Topics */}
      <section data-testid="speaking-topics" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-10">Temáticas principales</p>
          <div className="space-y-0">
            {topics.map((t, i) => (
              <div
                key={t.title}
                data-testid={`topic-${i}`}
                className="py-7 border-b border-border grid grid-cols-1 md:grid-cols-3 gap-4 hover:bg-card transition-colors px-2 -mx-2"
              >
                <div className="md:col-span-1 flex items-start gap-4">
                  <span className="font-mono text-border text-sm">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-serif text-lg leading-snug">{t.title}</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed md:col-span-2 md:pl-6">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section data-testid="speaking-audience" className="py-24 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="label-mono text-muted-foreground mb-6">Para quién</p>
              <h2 className="font-serif text-3xl font-normal mb-8 leading-tight">
                Organizaciones que quieren pensar mejor, no solo escuchar mejor.
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {audience.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                    {a}
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/j79m289m_IMG_0464.jpeg"
              alt="Eduardo Delgado en panel"
              className="w-full max-h-[400px] object-cover grayscale-[10%]"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="speaking-cta" className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-normal mb-6 max-w-xl mx-auto leading-tight">
            ¿Tienes un evento, sesión o colaboración en mente?
          </h2>
          <p className="text-muted-foreground mb-10 max-w-md mx-auto text-sm leading-relaxed">
            Escribe con un motivo concreto: tipo de evento, fecha, formato, audiencia esperada
            y lo que quieres que los asistentes se lleven.
          </p>
          <Link
            to="/contacto"
            data-testid="speaking-contact-cta"
            className="label-mono bg-primary text-primary-foreground px-10 py-4 hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
          >
            Proponer colaboración <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
